import { useCallback, useEffect, useState } from 'react'
import { IconChevronRightSmall, IconTaxi } from '@holbonkit/icons'
import { Box, Button, Text, toast, Toaster } from '@holbonkit/ui'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { clsx } from 'clsx'

import { useGetSelfEmployedFinance, useListSelfEmployedTransactions } from '@/shared/api'
import { useAppNavbar } from '@/shared/hooks/use-app-navbar'
import {
    setLocalStorageValue,
    type TWithdrawalSmzOnboardingKeys,
    useLocalStorage,
} from '@/shared/hooks/use-local-storage'
import { dismissApp } from '@/shared/lib/app-bridge'
import { navigateToTaxoparkCabinet } from '@/shared/lib/navigate'
import { Carousel } from '@/shared/ui'

import { CustomStories, WithdrawalSmzStories } from '@/widgets'

import {
    AboutBottomSheet,
    ConfirmBottomSheet,
    WarningBottomSheet,
} from './bottom-sheet/bottom-sheet'
import { CarouselContent } from './carousel-content/carousel-content'
import { History } from './history/history'
import { MethodSlot } from './method-slot/method-slot'
import { SecondaryBlock } from './secondary-block/secondary-block'
import { WithdrawalSmzLoading } from './withdrawal-smz-loading/withdrawal-smz-loading'

import styles from './withdrawal-smz.module.scss'

export const WithdrawalSmz = () => {
    useAppNavbar(' ', 'Назад', 'onNavbarBackButtonClick()')

    const navigate = useNavigate()
    const location = useLocation()

    const [activeSlide, setActiveSlide] = useState(0)
    const [isHistoryOpen, setIsHistoryOpen] = useState(false)
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
    const [isConfirmBottomSheetOpen, setIsConfirmBottomSheetOpen] = useState(false)
    const [isAboutBottomSheetOpen, setIsAboutBottomSheetOpen] = useState(false)
    const [isSubmitLoading, setIsSubmitLoading] = useState(false)

    const {
        data: selfEmployedFinance,
        isFetched: isSelfEmployedFinanceFetched,
        isError: isSelfEmployedFinanceError,
    } = useGetSelfEmployedFinance()

    const {
        data: selfEmployedTransactions,
        isFetching: isSelfEmployedTransactionsFetching,
        isError: isSelfEmployedTransactionsError,
        refetch: refetchSelfEmployedTransactions,
    } = useListSelfEmployedTransactions({
        limit: 3,
        cursor: undefined,
    })

    useEffect(() => {
        if (isSelfEmployedFinanceError) {
            navigate({
                to: '/error',
                search: {
                    title: 'Ошибка при загрузке данных о финансах',
                    description: 'Попробуйте позже',
                },
            })
        }
    }, [isSelfEmployedFinanceError, navigate])

    const [isPopoverUpcomingOpen, setIsPopoverUpcomingOpen] = useState(false)
    const [isPopoverWithdrawalOpen, setIsPopoverWithdrawalOpen] = useState(false)
    const [isPopoverDeductedOpen, setIsPopoverDeductedOpen] = useState(false)
    // Onboarding
    const { getValue: onboardingStatusStorage, setValue: setOnboardingStatusStorage } =
        useLocalStorage<TWithdrawalSmzOnboardingKeys>('withdrawal-smz-onboarding', 'firstShow')
    const showOnboarding =
        onboardingStatusStorage() === 'firstShow' || onboardingStatusStorage() === 'secondShow'
    const [showStories, setShowStories] = useState<boolean>(showOnboarding)
    const closeStories = useCallback(() => {
        if (onboardingStatusStorage() === 'firstShow') {
            setOnboardingStatusStorage('secondShow')
        } else {
            setOnboardingStatusStorage('showed')
        }
        setShowStories(false)
    }, [onboardingStatusStorage, setOnboardingStatusStorage])

    const { getValue: popoverUpcomingStorage, setValue: setPopoverUpcomingStorage } =
        useLocalStorage('withdrawal-smz-popover-upcoming', false)

    const { getValue: popoverDeductedStorage, setValue: setPopoverDeductedStorage } =
        useLocalStorage('withdrawal-smz-popover-deducted', false)

    const { getValue: popoverWithdrawalStorage, setValue: setPopoverWithdrawalStorage } =
        useLocalStorage('withdrawal-smz-popover-withdrawal', false)

    const accountLink = selfEmployedFinance?.payout_method ?? undefined
    const [seenAccountLink, setSeenAccountLink] = useState(accountLink)

    if (accountLink !== seenAccountLink) {
        setSeenAccountLink(accountLink)

        if (accountLink?.bank_id && !popoverWithdrawalStorage()) {
            setPopoverWithdrawalStorage(true)
            setIsPopoverWithdrawalOpen(true)
        }
    }

    const [entryWarningChecked, setEntryWarningChecked] = useState(false)

    if (isSelfEmployedFinanceFetched && !entryWarningChecked) {
        setEntryWarningChecked(true)

        const unavailableReason = selfEmployedFinance?.withdrawal.unavailable_reason

        if (
            unavailableReason === 'fns_unavailable' ||
            unavailableReason === 'not_self_employed' ||
            unavailableReason === 'self_employed_status_lost'
        ) {
            setIsBottomSheetOpen(true)
        }
    }

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (activeSlide === 1 && !popoverDeductedStorage()) {
                setPopoverDeductedStorage(true)
                setIsPopoverDeductedOpen(true)
            }
        }, 500)

        return () => clearTimeout(timeout)
    }, [activeSlide, popoverDeductedStorage, setPopoverDeductedStorage])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (
                selfEmployedTransactions?.transactions?.length &&
                selfEmployedTransactions?.transactions.length > 0 &&
                !popoverUpcomingStorage() &&
                !isPopoverWithdrawalOpen
            ) {
                setPopoverUpcomingStorage(true)
                setIsPopoverUpcomingOpen(true)
            }
        }, 500)

        return () => clearTimeout(timeout)
    }, [
        selfEmployedTransactions,
        popoverUpcomingStorage,
        setPopoverUpcomingStorage,
        isPopoverWithdrawalOpen,
    ])

    const handleNavbarBackButtonClick = useCallback(() => {
        if (isHistoryOpen) {
            setIsHistoryOpen(false)
        } else {
            dismissApp()
        }
    }, [isHistoryOpen])

    const sbpType = location.search.sbpType
    const sbpUpdated = location.search.sbpUpdated
    const sbpUpdatedValue = location.search.sbpUpdatedValue
    const from = location.search.from

    useEffect(() => {
        const payoutMethod = selfEmployedFinance?.payout_method

        setLocalStorageValue(
            'withdrawal-funds-account-link',
            payoutMethod ? { ...payoutMethod } : undefined,
        )
        if (sbpType === 'created' && !isSelfEmployedFinanceFetched) {
            toast.success('Счёт СБП добавлен')
        }
        if (sbpType === 'deleted' && !isSelfEmployedFinanceFetched) {
            toast.success('Счёт СБП удалён')
        }
        if (sbpType === 'updated' && !isSelfEmployedFinanceFetched) {
            switch (sbpUpdated) {
                case 'bank_phone': {
                    toast.success(`Номер телефона и счёт СБП обновлены`)
                    break
                }
                case 'bank': {
                    toast.success(`Счёт СБП изменён на ${sbpUpdatedValue}`)
                    break
                }
                case 'phone': {
                    toast.success('Номер телефона обновлён')
                    break
                }
                case 'email': {
                    toast.success('Адрес электронной почты обновлён')
                    break
                }
            }
        }
    }, [sbpType, sbpUpdated, sbpUpdatedValue, isSelfEmployedFinanceFetched, selfEmployedFinance])

    useEffect(() => {
        globalThis.window.onNavbarBackButtonClick = handleNavbarBackButtonClick

        return () => {
            delete globalThis.window.onNavbarBackButtonClick
        }
    }, [handleNavbarBackButtonClick])

    if (!isSelfEmployedFinanceFetched) {
        return <WithdrawalSmzLoading />
    }

    if (isHistoryOpen) {
        return <History />
    }

    // TODO: Remove this after testing
    const description = () => {
        return (
            <Box>
                <Button
                    variant='secondary'
                    onClick={() =>
                        navigate({
                            to: '/withdrawal-smz/result',
                            search: {
                                amount: selfEmployedFinance?.balance.available ?? 0,
                                status: 'in_progress',
                            },
                        })
                    }
                >
                    В процессе
                </Button>
                <Button
                    variant='secondary'
                    onClick={() =>
                        navigate({
                            to: '/withdrawal-smz/result',
                            search: {
                                amount: selfEmployedFinance?.balance.available ?? 0,
                                status: 'error_sbp',
                            },
                        })
                    }
                >
                    Ошибка счёта СБП
                </Button>
                <Button
                    variant='secondary'
                    onClick={() =>
                        navigate({
                            to: '/withdrawal-smz/result',
                            search: {
                                amount: selfEmployedFinance?.balance.available ?? 0,
                                status: 'error',
                            },
                        })
                    }
                >
                    Ошибка
                </Button>
            </Box>
        )
    }

    const handleSubmit = () => {
        setIsSubmitLoading(true)
        setTimeout(() => {
            toast.warning('В какой экран перекинуть? (TODO: Remove this after testing)', {
                timeout: 0,
                description: description(),
            })
            setIsSubmitLoading(false)
        }, 1000)
    }

    return (
        <>
            {showStories && from !== 'account-link' && from !== 'result' && (
                <CustomStories setShowStories={closeStories} stories={WithdrawalSmzStories} />
            )}
            {(!showStories || from === 'account-link' || from === 'result') && (
                <Box
                    className={clsx(
                        styles['withdrawal-smz'],
                        (isPopoverUpcomingOpen ||
                            isPopoverWithdrawalOpen ||
                            isPopoverDeductedOpen) &&
                            styles['withdrawal-smz__not-clickable'],
                    )}
                >
                    <Box>
                        <Carousel
                            activeIndex={activeSlide}
                            className={styles.carousel}
                            setActiveIndex={setActiveSlide}
                            items={CarouselContent({
                                balances: {
                                    available: selfEmployedFinance?.balance.available ?? 0,
                                    upcoming: selfEmployedFinance?.balance.upcoming ?? 0,
                                    deducted: selfEmployedFinance?.deducted_this_month ?? 0,
                                },
                                popovers: {
                                    upcoming: {
                                        isOpen: isPopoverUpcomingOpen,
                                        setIsOpen: setIsPopoverUpcomingOpen,
                                    },
                                    withdrawal: {
                                        isOpen: isPopoverWithdrawalOpen,
                                        setIsOpen: setIsPopoverWithdrawalOpen,
                                    },
                                    deducted: {
                                        isOpen: isPopoverDeductedOpen,
                                        setIsOpen: setIsPopoverDeductedOpen,
                                    },
                                },
                                onWithdraw: () => setIsConfirmBottomSheetOpen(true),
                                onAbout: () => setIsAboutBottomSheetOpen(true),
                            })}
                        />

                        <MethodSlot
                            isOpen={activeSlide === 0}
                            payoutMethod={accountLink}
                            isDisabled={
                                isPopoverUpcomingOpen ||
                                isPopoverWithdrawalOpen ||
                                isPopoverDeductedOpen
                            }
                            onClick={() => {
                                navigate({
                                    to: '/withdrawal-smz/account-link',
                                    search: {
                                        edit_mode: Boolean(accountLink?.bank_id),
                                    },
                                })
                            }}
                        />
                    </Box>
                    <Box className={styles['withdrawal-smz__operations']}>
                        <SecondaryBlock
                            isError={isSelfEmployedTransactionsError}
                            isLoading={isSelfEmployedTransactionsFetching}
                            latestOperations={selfEmployedTransactions?.transactions ?? []}
                            refetch={refetchSelfEmployedTransactions}
                            withdrawalIsAvailable={
                                selfEmployedFinance?.withdrawal.is_available ?? false
                            }
                            withdrawalUnavailableReason={
                                selfEmployedFinance?.withdrawal.unavailable_reason
                            }
                            onConfirmSmz={() => setIsBottomSheetOpen(true)}
                            onOpenHistory={() => setIsHistoryOpen(!isHistoryOpen)}
                        />
                    </Box>
                    {selfEmployedFinance?.partner_account.has_balance && (
                        <Box
                            className={styles['withdrawal-smz__partner-account']}
                            onClick={() => {
                                navigateToTaxoparkCabinet()
                            }}
                        >
                            <IconTaxi size={24} />
                            <Text style={{ width: '100%' }} variant='body-m-16-medium'>
                                На счёте партнёра
                            </Text>
                            <IconChevronRightSmall
                                color='var(--text-and-icon-secondary)'
                                size={24}
                            />
                        </Box>
                    )}

                    <WarningBottomSheet
                        isBottomSheetOpen={isBottomSheetOpen}
                        setIsBottomSheetOpen={setIsBottomSheetOpen}
                        type={
                            selfEmployedFinance?.withdrawal.unavailable_reason as
                                | 'fns_unavailable'
                                | 'not_self_employed'
                                | 'self_employed_status_lost'
                        }
                    />
                    <ConfirmBottomSheet
                        amount={selfEmployedFinance?.balance.available ?? 0}
                        isBottomSheetOpen={isConfirmBottomSheetOpen}
                        isSubmitLoading={isSubmitLoading}
                        phoneNumber={selfEmployedFinance?.payout_method?.phone ?? ''}
                        setIsBottomSheetOpen={setIsConfirmBottomSheetOpen}
                        onSubmit={handleSubmit}
                    />
                    <AboutBottomSheet
                        isBottomSheetOpen={isAboutBottomSheetOpen}
                        setIsBottomSheetOpen={setIsAboutBottomSheetOpen}
                    />
                </Box>
            )}
            <Toaster position='bottom-center' />
        </>
    )
}
